import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Receipt as ReceiptIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import axios from 'axios';
import { format } from 'date-fns';

// Use env var if set, otherwise use relative URL
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography color="text.secondary" gutterBottom variant="body2" fontWeight="medium">
            {title}
          </Typography>
          <Typography variant="h4" component="div" fontWeight="bold" sx={{ mt: 1 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: `${color}.light`,
            color: `${color}.dark`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const UserAnalytics = () => {
  const [period, setPeriod] = useState('30');
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, error, refetch } = useQuery(
    ['user-analytics', period],
    async () => {
      const response = await axios.get(`${API_BASE_URL}/dashboard/user-analytics`, {
        params: { period },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data.data;
    },
    {
      refetchInterval: 60000, // Refetch every minute
      retry: 1,
    }
  );

  const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(value || 0);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return format(new Date(dateString), 'dd MMM yyyy, hh:mm a');
    } catch {
      return dateString;
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch {
      return dateString;
    }
  };

  // Filter users based on search query
  const filteredUsers = data?.users?.filter((user) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.phone?.toLowerCase().includes(query) ||
      user.full_name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.address?.toLowerCase().includes(query) ||
      user.city?.toLowerCase().includes(query)
    );
  }) || [];

  // Prepare chart data
  const loginChartData = data?.loginStats?.map((stat) => ({
    date: formatDateShort(stat.date),
    'Unique Logins': stat.unique_logins,
    'Total Logins': stat.total_logins,
  })) || [];

  const registrationChartData = data?.registrationStats?.map((stat) => ({
    date: formatDateShort(stat.date),
    'New Registrations': stat.registrations,
  })) || [];

  const deviceChartData = data?.deviceStats?.map((stat) => ({
    name: stat.device_info || 'Unknown',
    value: stat.count,
    users: stat.unique_users,
  })) || [];

  const locationChartData = data?.locationStats?.slice(0, 10).map((stat) => ({
    name: stat.location || 'Unknown',
    value: stat.count,
    users: stat.unique_users,
  })) || [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load user analytics. Please try again.
          <br />
          {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          User Analytics & Login Statistics
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select value={period} label="Period" onChange={(e) => setPeriod(e.target.value)}>
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="180">Last 6 months</MenuItem>
              <MenuItem value="365">Last year</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh Data">
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={formatNumber(data?.totals?.total_users)}
            icon={<PeopleIcon sx={{ fontSize: 32 }} />}
            color="primary"
            subtitle={`${formatNumber(data?.totals?.new_registrations)} new in ${period} days`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Logins"
            value={formatNumber(data?.totals?.total_logins)}
            icon={<LoginIcon sx={{ fontSize: 32 }} />}
            color="success"
            subtitle={`${formatNumber(data?.totals?.unique_logins)} unique users`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="New Registrations"
            value={formatNumber(data?.totals?.new_registrations)}
            icon={<PersonAddIcon sx={{ fontSize: 32 }} />}
            color="info"
            subtitle={`In last ${period} days`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Users with Bills"
            value={formatNumber(data?.totals?.users_with_bills)}
            icon={<ReceiptIcon sx={{ fontSize: 32 }} />}
            color="warning"
            subtitle={`${formatNumber(data?.totals?.total_bills)} total bills`}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Login Trends" />
          <Tab label="Registration Trends" />
          <Tab label="Device Statistics" />
          <Tab label="Location Statistics" />
          <Tab label="User Details" />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Login Activity Over Time
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={loginChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Line type="monotone" dataKey="Unique Logins" stroke="#0088FE" strokeWidth={2} />
                <Line type="monotone" dataKey="Total Logins" stroke="#00C49F" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {tabValue === 1 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Registration Trends
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={registrationChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="New Registrations" fill="#8884D8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Logins by Device Type
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={deviceChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {deviceChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Device Statistics Table
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Device Type</strong></TableCell>
                        <TableCell align="right"><strong>Logins</strong></TableCell>
                        <TableCell align="right"><strong>Unique Users</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deviceChartData.map((device, index) => (
                        <TableRow key={index}>
                          <TableCell>{device.name}</TableCell>
                          <TableCell align="right">{formatNumber(device.value)}</TableCell>
                          <TableCell align="right">{formatNumber(device.users)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Logins by Location
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={locationChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={150} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="value" fill="#8884D8" name="Total Logins" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Location Statistics Table
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Location</strong></TableCell>
                        <TableCell align="right"><strong>Logins</strong></TableCell>
                        <TableCell align="right"><strong>Unique Users</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {locationChartData.map((location, index) => (
                        <TableRow key={index}>
                          <TableCell>{location.name}</TableCell>
                          <TableCell align="right">{formatNumber(location.value)}</TableCell>
                          <TableCell align="right">{formatNumber(location.users)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tabValue === 4 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                User Details with Services Used
              </Typography>
              <TextField
                size="small"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300 }}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Mobile Number</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Address</strong></TableCell>
                    <TableCell><strong>City/State</strong></TableCell>
                    <TableCell align="center"><strong>Login Count</strong></TableCell>
                    <TableCell align="center"><strong>Bills Count</strong></TableCell>
                    <TableCell><strong>Last Login</strong></TableCell>
                    <TableCell><strong>Registered</strong></TableCell>
                    <TableCell><strong>Services Used</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography color="text.secondary">No users found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography>{user.phone || 'N/A'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {user.full_name || 'N/A'}
                          </Typography>
                          {user.email && (
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                            <LocationIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                            <Box>
                              <Typography variant="body2">
                                {user.address || 'N/A'}
                              </Typography>
                              {user.pincode && (
                                <Typography variant="caption" color="text.secondary">
                                  PIN: {user.pincode}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {user.city || 'N/A'}
                            {user.city && user.state && ', '}
                            {user.state || ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={user.login_count || 0}
                            size="small"
                            color={user.login_count > 0 ? 'success' : 'default'}
                            icon={<LoginIcon />}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={user.bills_count || 0}
                            size="small"
                            color={user.bills_count > 0 ? 'primary' : 'default'}
                            icon={<ReceiptIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(user.last_session_at || user.last_login)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateShort(user.registered_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {user.services && user.services.length > 0 ? (
                            <Box>
                              {user.services.slice(0, 2).map((service, idx) => (
                                <Chip
                                  key={idx}
                                  label={`${service.shop_name || 'Service'} (₹${service.amount?.toFixed(2) || 0})`}
                                  size="small"
                                  sx={{ mb: 0.5, mr: 0.5 }}
                                />
                              ))}
                              {user.services.length > 2 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{user.services.length - 2} more
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No services used
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredUsers.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredUsers.length} of {data?.users?.length || 0} users
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default UserAnalytics;













